import { z } from 'zod';

// Pagination parameters schema
export const PaginationParamsSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(5000).default(50),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
});

export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

// Pagination metadata schema
export const PaginationMetaSchema = z.object({
  currentPage: z.number().int().min(1),
  itemsPerPage: z.number().int().min(1),
  totalItems: z.number().int().min(0),
  totalPages: z.number().int().min(0),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

// Generic response schema factory
export const createResponseSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    data: schema,
    message: z.string().optional(),
  });

// Generic paginated response schema - updated to match our pattern
export const createPaginatedResponseSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    data: z.array(schema),
    meta: PaginationMetaSchema,
    message: z.string().optional(),
  });

export type PaginatedResponse<T> = {
  data: T[];
  meta: PaginationMeta;
};

// Helper function to calculate pagination metadata
export const createPaginationMeta = (
  page: number,
  limit: number,
  totalItems: number,
): PaginationMeta => {
  const totalPages = Math.ceil(totalItems / limit);
  
  return {
    currentPage: page,
    itemsPerPage: limit,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
};

// Helper function to convert pagination params to TypeORM options
export const getPaginationOptions = (params: PaginationParams) => {
  // Ensure page and limit are numbers
  const page = Number(params.page) || 1;
  const limit = Number(params.limit) || 10;
  const { sortBy, sortOrder } = params;
  
  const skip = (page - 1) * limit;
  const take = limit;
  
  // Create orderBy object if sortBy is provided
  let orderBy: Record<string, 'asc' | 'desc'> | undefined = undefined;
  
  if (sortBy) {
    // Ensure sortOrder is either 'asc' or 'desc'
    const validSortOrder = sortOrder === 'asc' ? 'asc' : 'desc';
    orderBy = { [sortBy]: validSortOrder };
  }
  
  return {
    skip,
    take,
    orderBy,
  };
}; 